/**
 * COMPONENTE DE NOTIFICAÇÃO DE PAGAMENTO PIX
 * 
 * Toast com contador de 60 segundos que aparece quando um PIX é confirmado
 * Caminho: frontend/src/components/NotificacaoPagamento.jsx
 */
import React, { useState, useEffect } from 'react';
import { X, CheckCircle, DollarSign, User, Clock } from 'lucide-react';

const NotificacaoPagamento = ({ notificacao, onClose }) => {
  const [tempoRestante, setTempoRestante] = useState(60); // 60 segundos
  const [progresso, setProgresso] = useState(100);

  useEffect(() => {
    // Contador regressivo
    const intervalo = setInterval(() => {
      setTempoRestante((prev) => {
        if (prev <= 1) {
          clearInterval(intervalo);
          onClose(); // Auto-fechar
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Atualizar barra de progresso
    const intervaloProgresso = setInterval(() => {
      setProgresso((prev) => {
        const novoProgresso = (tempoRestante / 60) * 100;
        return Math.max(0, novoProgresso);
      });
    }, 100);

    return () => {
      clearInterval(intervalo);
      clearInterval(intervaloProgresso);
    };
  }, [onClose, tempoRestante]);

  const formatarValor = (valor) => {
    if (!valor) return 'R$ 0,00';
    return `R$ ${parseFloat(valor).toFixed(2).replace('.', ',')}`;
  };

  const formatarHora = () => {
    const agora = new Date();
    return agora.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className="bg-white rounded-xl shadow-2xl border-2 border-green-400 overflow-hidden w-96">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-white">
              <h3 className="font-bold text-lg">PIX Confirmado!</h3>
              <p className="text-xs text-green-100">{formatarHora()}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-green-700 rounded-lg p-1.5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-4 space-y-3">
          {/* Valor */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-2 text-green-700 text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              Valor Pago
            </div>
            <div className="text-3xl font-bold text-green-900">
              {formatarValor(notificacao.dados.valor_pago)}
            </div>
          </div>

          {/* Cliente */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 text-xs mb-2">
              <User className="w-3.5 h-3.5" />
              Cliente
            </div>
            <div className="font-semibold text-gray-900">
              {notificacao.dados.nome_cliente}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Cód: {notificacao.dados.codigo_cliente} | 
              Ordem: {notificacao.dados.ordem_cliente}
            </div>
          </div>

          {/* Contador */}
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <Clock className="w-4 h-4" />
            <span>Fechará em {tempoRestante}s</span>
          </div>

          {/* Barra de Progresso */}
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-green-500 h-full transition-all duration-1000 ease-linear"
              style={{ width: `${progresso}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-t border-gray-200">
          <span className="text-xs text-gray-500">
            ID: {notificacao.dados.pedido_id?.substring(0, 12)}...
          </span>
          <button
            onClick={onClose}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
          >
            Fechar agora
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificacaoPagamento;